/* eslint-disable @typescript-eslint/no-explicit-any */
import MenuItem from '@mui/material/MenuItem'
import Select, { SelectProps } from '@mui/material/Select'
import { Controller, FieldError, RegisterOptions } from 'react-hook-form'
import {
  DROPDOWN_MENU_PROPS,
  DropdownItemContent
} from 'src/components/Inputs/dropdownMenu'
import FieldLabel from 'src/components/Inputs/FieldLabel'

// Kept exported for backwards compatibility with consumers that still spread it.
export const SX_SELECT = DROPDOWN_MENU_PROPS.sx

interface StyledSelectProps extends Omit<SelectProps, 'error'> {
  black?: number
  error?: boolean
}

export const StyledSelect = ({
  black,
  error,
  className,
  ...rest
}: StyledSelectProps) => {
  // black variant classes
  const colorClass = black ? 'text-white-500' : 'text-ink-700'
  const bgClass = black ? 'bg-ink-400' : 'bg-white-500'
  const borderBase = black
    ? 'border border-solid border-transparent'
    : 'border border-solid border-ink-100/40'
  const hoverBorder = black
    ? 'hover:border-transparent'
    : 'hover:border-ink-100/60'
  const focusBorder = black
    ? '[&.Mui-focused]:border-transparent'
    : '[&.Mui-focused]:border-blue-500'
  const focusShadow = black
    ? '[&.Mui-focused]:shadow-none'
    : '[&.Mui-focused]:shadow-[0_0_0_3px_#D8F1FF]'
  const iconColor = black
    ? '[&_.MuiSelect-icon]:text-white-500'
    : '[&_.MuiSelect-icon]:text-blue-500'

  // Error state
  const errorClasses = error
    ? 'border-red-500! hover:border-red-500 [&.Mui-focused]:border-red-500 [&.Mui-focused]:shadow-[0_0_0_3px_rgba(250,126,141,0.2)]'
    : ''

  return (
    <Select
      error={error}
      className={`font-normal rounded-[10px] h-[40px] transition-[border-color,box-shadow] duration-200 ease-linear [&>.MuiOutlinedInput-notchedOutline]:border-none [&_.MuiOutlinedInput-notchedOutline]:border-none [&_.MuiSelect-select[aria-expanded=true]~.icon_i]:rotate-180 [&_.MuiSelect-select]:py-[10px] [&_.MuiSelect-select]:pl-[14px] [&_.MuiSelect-select]:pr-[36px] ${colorClass} ${bgClass} ${borderBase} ${hoverBorder} ${focusBorder} ${focusShadow} ${iconColor} ${errorClasses} ${className ?? ''}`.trim()}
      {...rest}
    />
  )
}

type EntryValue =
  | {
      label: string
      description?: string
      icon?: string
      src?: string
      deprecated?: boolean
    }
  | string
  | React.ReactNode

type IProps = SelectProps & {
  id?: string
  topLabel?: string
  topDescription?: string
  topLabelIcon?: string
  control?: any
  error?: FieldError | boolean | undefined
  rules?: RegisterOptions
  entries: {
    [key: string]: EntryValue
  }
  black?: boolean
}

function isEntryObject(value: EntryValue): value is {
  label: string
  description?: string
  icon?: string
  src?: string
  deprecated?: boolean
} {
  return (
    typeof value === 'object' && value !== null && 'label' in (value as object)
  )
}

function renderEntry(
  key: string,
  value: EntryValue,
  currentValue: unknown,
  hasIconColumn: boolean
) {
  const isSelected = currentValue === key

  if (isEntryObject(value)) {
    return (
      <MenuItem
        key={key}
        value={key}
        disabled={value.deprecated}
        className={value.deprecated ? '[&.Mui-disabled]:opacity-50' : ''}
      >
        <DropdownItemContent
          label={value.label}
          description={value.description}
          icon={value.icon}
          src={value.src}
          deprecated={value.deprecated}
          isSelected={isSelected}
          hasIconColumn={hasIconColumn}
        />
      </MenuItem>
    )
  }

  return (
    <MenuItem key={key} value={key}>
      <DropdownItemContent
        label={value as React.ReactNode}
        isSelected={isSelected}
        hasIconColumn={hasIconColumn}
      />
    </MenuItem>
  )
}

export default function BaseSelect({
  id,
  rules,
  entries,
  topLabel,
  topLabelIcon,
  topDescription,
  control,
  black,
  defaultValue,
  // Fill the container by default — same reason as BaseTextField: MUI's Select
  // is inline-flex at its intrinsic width otherwise, which renders stubby on
  // mobile whenever a caller forgets `fullWidth`.
  fullWidth = true,
  ...SelectProps
}: IProps) {
  // If any entry has an icon/src, reserve the icon column on every row so labels align.
  const hasIconColumn = Object.values(entries).some(
    (value) => isEntryObject(value) && Boolean(value.icon || value.src)
  )

  return (
    <div>
      <FieldLabel
        topLabel={topLabel}
        topLabelIcon={topLabelIcon}
        topDescription={topDescription}
        required={SelectProps.required}
      />
      {id && control ? (
        <Controller
          name={id}
          control={control}
          rules={rules}
          defaultValue={defaultValue}
          render={({ field }) => (
            <StyledSelect
              MenuProps={DROPDOWN_MENU_PROPS}
              {...SelectProps}
              {...field}
              onChange={(e, value) => {
                field.onChange(e)
                SelectProps?.onChange?.(e, value)
              }}
              black={black ? 1 : 0}
              fullWidth={fullWidth}
              error={!!SelectProps.error}
              disabled={SelectProps.disabled}
            >
              {Object.entries(entries).map(([key, value]) =>
                renderEntry(key, value, field.value, hasIconColumn)
              )}
            </StyledSelect>
          )}
        />
      ) : (
        <StyledSelect
          MenuProps={DROPDOWN_MENU_PROPS}
          {...SelectProps}
          defaultValue={defaultValue}
          black={black ? 1 : 0}
          fullWidth={fullWidth}
          error={!!SelectProps.error}
          disabled={SelectProps.disabled}
        >
          {Object.entries(entries).map(([key, value]) =>
            renderEntry(
              key,
              value,
              SelectProps.value ?? defaultValue,
              hasIconColumn
            )
          )}
        </StyledSelect>
      )}
    </div>
  )
}
